<template>
  <pui-file-upload-dialog
    ref="dialog"
    :canImport="canImport"
    :showError="errorLoadingConfig"
    :reloadAfterError="reloadAfterError"
    :showForm="!!loadingConfig || !!form"
    @changeFiles="onChangeFiles"
    @import="onImport"
    class="poster-file-upload-dialog poster-file-upload-dialog__root"
  >
    <template #form>
      <pui-spinner v-if="loadingConfig"></pui-spinner>
      <template v-else>
        <div class="bea-variables step1 padding">
          <div>
            <div v-if="!families || families.length === 0" class="validation-alert">
              <p>{{ $t('poster.family_selector.no_families.message') }}</p>
            </div>
            <pui-family-selector
              v-else
              :families="families"
              :selectedFamily="family"
              :showLabel="true"
              @change="onFamilyChanged"
            />
            <pui-type-selector
              :rootTypes="types"
              :selectedType="type"
              :showLabel="true"
              @change="onTypeChanged"
            />

            <div v-if="!formatGroups || !formatGroups.length" class="validation-alert">
              <p>{{ $t('poster.upload.dialog.format.none') }}</p>
            </div>
            <pui-common-select
              v-else
              :display="piivoTranslate"
              :label="$t('poster.upload.dialog.format.label')"
              :options="formatGroups"
              :value="format"
              :multiple="false"
              :taggable="false"
              :allowGrouping="true"
              :clearable="false"
              :isLoading="loadingFormatGroups"
              @select="onFormatChange"
            />

            <pui-input
              v-model="copies"
              :min="MIN_COPIES"
              :max="MAX_COPIES"
              :step="1"
              :label="$t('poster.upload.dialog.copies.label')"
              type="number"
            />

            <pui-input
              v-model="comment"
              :label="$t('poster.upload.dialog.comment.label')"
              :textarea="true"
              :rows="3"
              type="text"
            >
            </pui-input>

            <div v-if="form && form.attributeGroups && values" class="form-separator"></div>
          </div>
        </div>

        <pui-flex
          v-if="form && form.attributeGroups && values"
          class="poster-file-upload-dialog__attributes-wrapper"
          flex="1"
          direction="column"
        >
          <pui-attribute-group-panel
            v-for="group in form.attributeGroups"
            :key="group.itemId"
            :group="group"
          >
            <template v-if="group.attributes">
              <pui-attribute-panel
                v-for="attribute in group.attributes"
                :ref="`formAttribute_${attribute.alias}`"
                :key="attribute.itemId"
                :attribute="attribute"
                :value="values[attribute.itemId] ? values[attribute.itemId].value : undefined"
                :loadOptionsFunction="getPossibleValues"
                @valueInput="onValueInput(attribute, $event)"
                @valueChanged="onValueChanged(attribute, $event)"
                :importExternalFiles="importExternalFiles"
              />
            </template>
          </pui-attribute-group-panel> </pui-flex
      ></template>
    </template>
  </pui-file-upload-dialog>
</template>

<script>
import Vue from 'vue';

import services from '../../../../core/services';
import { areAllAttributesValid } from '../../../common/helpers/attributeValidators';
import {
  getAttributeDefaultValue,
  iterateForm,
  updateOn,
} from '../../../common/helpers/formsHelper';
import puiFamilySelector from '../../components/selector/FamilySelector.vue';
import puiTypeSelector from '../../components/selector/TypeSelector.vue';
import { DEFAULT_SIGNAGE_SOURCE, MAX_COPIES, MIN_COPIES, Statutes } from '../../constants';
import { POSTER_FILE_UPLOAD_DEFAULT_TASK, POSTER_UPLOAD_FORM_ACTION } from '../../constants/upload';
import { getPosterService } from '../../services';
import posterFileUploadNotification from './FileUploadNotification.vue';
import { AttributeTypes } from '../../../common/constants';

export default {
  components: {
    puiFamilySelector,
    puiTypeSelector,
  },
  data() {
    return {
      files: [],
      // Main Config
      loadingConfig: true,
      errorLoadingConfig: false,
      families: [],
      familiy: null,
      types: [],
      type: null,
      loadingFormatGroups: false,
      formatGroups: [],
      format: null,
      copies: 0,
      comment: '',
      // Form
      importExternalFileCount: 0,
      areAttributesValid: true,
      form: null,
      /**
       * Maps an attribute's itemId to a value object
       * @type {{[attributeItemId: string]: {attributeId: string; attributeAlias: string, type: string; value: *}}}
       */
      values: {},
      // Constants
      MIN_COPIES,
      MAX_COPIES,
    };
  },
  computed: {
    /**
     * @returns {boolean} if the user can execute an import
     */
    canImport() {
      return (
        !!this.files.length &&
        !!this.format &&
        this.copies > 0 &&
        !!this.areAttributesValid &&
        !this.importExternalFileCount
      );
    },
  },
  methods: {
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    async open() {
      this.$refs.dialog.open();
      this.resetState();
      this.initFormDefaultValues();

      // Restart the default family>type>format chain
      if (this.families.length) {
        this.familiy = this.families[0];
        await this.retrieveTypesForCurrentFamily();
      }
    },
    /**
     * Removes the error state
     */
    reloadAfterError() {
      this.init();
    },
    /**
     * Initializes the state of the dialog
     * for a new import
     */
    resetState() {
      this.files = [];
      this.familiy = null;
      this.type = null;
      this.format = null;
      this.copies = 0;
      this.comment = '';

      this.importExternalFileCount = 0;
      this.areAttributesValid = true;
      this.values = {};
    },
    /**
     * Loads all the data required for an upload
     */
    async init() {
      this.loadingConfig = true;
      this.errorLoadingConfig = false;
      try {
        await Promise.all([this.getFamilies(), this.getForm()]);
      } catch (err) {
        this.errorLoadingConfig = true;
      }
      this.loadingConfig = false;
    },
    // FAMILY/TYPES/FORMATS
    /**
     * Loads all families
     */
    async getFamilies() {
      this.families = await getPosterService('templates').loadFamilies();
      this.family = this.families.length ? this.families[0] : null;
      await this.retrieveTypesForCurrentFamily();
    },
    /**
     * Loads the types for the current family
     */
    async retrieveTypesForCurrentFamily() {
      if (!this.family) {
        this.types = [];
        this.type = null;
        return;
      }

      this.types = await getPosterService('templates').loadFamilyRootTypes(this.family.itemId);
      this.type = getPosterService('templates').getDefaultTemplateType(this.types);
      await this.retrieveFormatsForCurrentType();
    },
    /**
     * Loads the format groups for the current type
     */
    async retrieveFormatsForCurrentType() {
      if (!this.type) {
        this.formatGroups = [];
        this.format = null;
        return;
      }

      try {
        this.loadingFormatGroups = true;
        const formatGroups = await getPosterService('templates').loadTypeFormatGroups(
          this.type.itemId
        );

        formatGroups.forEach(group => {
          // Pui-select needs children on "children" prop
          group.children = group.formats;
        });

        this.formatGroups = formatGroups;
        this.format = getPosterService('templates').getDefaultTemplateTypeFormat(
          this.formatGroups,
          this.format ? this.format.itemId : null
        );

        this.loadingFormatGroups = false;
      } catch (err) {
        this.loadingFormatGroups = false;
        throw err;
      }
    },
    /**
     * Handles the family change
     *
     * @param {object} family - the new family
     */
    onFamilyChanged(family) {
      this.family = family;
      this.retrieveTypesForCurrentFamily();
    },
    /**
     * Handles the type change
     *
     * @param {object} type - the new type
     */
    onTypeChanged(type) {
      this.type = type;
      this.retrieveFormatsForCurrentType();
    },
    /**
     * Handles the format change
     *
     * @param {object} format - the new format
     */
    onFormatChange(format) {
      this.format = format;
    },
    // FORM INIT
    async getForm() {
      let actionConf = null;

      try {
        actionConf = await getPosterService('sendPosters').retrieveActionConfiguration(
          POSTER_UPLOAD_FORM_ACTION
        );
      } catch (err) {
        getPosterService('sendPosters').clearActionConfiguration(POSTER_UPLOAD_FORM_ACTION);
        throw err;
      }

      if (actionConf && actionConf.form) {
        try {
          this.form = await getPosterService('sendPosters').retrieveActionForm(
            POSTER_UPLOAD_FORM_ACTION
          );
        } catch (err) {
          getPosterService('sendPosters').clearActionForm(POSTER_UPLOAD_FORM_ACTION);
          throw err;
        }
      }
    },
    // FORM
    /**
     * Called when the array of files to upload changes
     */
    onChangeFiles(files) {
      this.files = files;
      this.checkFormIsValid();
    },
    /**
     * Initializes the values object with the form's
     * default values
     */
    initFormDefaultValues() {
      if (!this.form) {
        return;
      }

      iterateForm(this.form, attribute => {
        const defaultValue = getAttributeDefaultValue(attribute);
        this.$set(this.values, attribute.itemId, {
          attributeId: attribute.itemId,
          attributeAlias: attribute.alias,
          type: attribute.type,
          value: defaultValue,
        });
      });
    },
    /**
     * @returns {{attributeId: string; attributeAlias: string, type: string; value: *}[]} the values
     * as an array of attribute value objects
     */
    valuesToArray() {
      return Object.values(this.values);
    },
    /**
     * Checks if the form is valid and stores the result in 'this.areAttributesValid'
     */
    checkFormIsValid() {
      const checkResult = areAllAttributesValid(this.values, this.form, {
        validateInvisibleItems: false,
        breakOnFirstError: true,
        attributeIdProp: 'itemId',
        getAttributeValue: (values, attribute) =>
          values[attribute.itemId] ? values[attribute.itemId].value : undefined,
      });
      const { errors } = checkResult;
      this.areAttributesValid = Object.keys(errors).length === 0;
    },
    /**
     * Updates attribute value
     *
     * @param {object} attribute - the attribute whose value changed
     * @param {Object} newValue - New attribute value
     */
    onValueInput(attribute, value) {
      if (this.values[attribute.id]) {
        this.values[attribute.id].value = value;
      } else {
        this.$set(this.values, attribute.itemId, {
          attributeId: attribute.itemId,
          attributeAlias: attribute.alias,
          type: attribute.type,
          value: value,
        });
      }
    },
    /**
     * Updates attribute value and checks form
     *
     * @param {object} attribute - the attribute whose value changed
     * @param {Object} newValue - New attribute value
     */
    onValueChanged(attribute, value) {
      this.onValueInput(attribute, value);
      this.startUpdateOn(attribute);
      this.checkFormIsValid();
    },
    /**
     * Launches the "updateOn" mechanism for the attribute
     *
     * @param {object} attribute - the attribute whose updateOn to trigger
     */
    startUpdateOn(attribute) {
      const actionCb = cbAttr => {
        if (cbAttr.type === AttributeTypes.LINKS) {
          this.$nextTick(() => {
            const attributePanel = this.$refs[`formAttribute_${cbAttr.alias}`];
            if (attributePanel != null && attributePanel[0] != null) {
              attributePanel[0].loadOptions().catch(() => null);
            }
          });
        }
      };

      updateOn(this.currentForm, attribute, actionCb);
    },
    /**
     * Imports external files to the attribute's storage
     *
     * @param {object} attribute - the attribute onto which we are uploading the files
     * @param {object[]} tempFiles - array of objects containing the file blobs to upload
     * @param {(tempFile, importedFile) => void} onFileUploadedCb - callback for when a file is finished uploading.
     * Receives the original temp file, and the uploaded file
     * @param {(cancelCb: (tempId: string) => void) => void} provideAbortCb - callback
     * that proves the cancel function
     */
    async importExternalFiles(attribute, tempFiles, onFileUploadedCb, provideAbortCb) {
      this.importExternalFileCount++;

      const importedFiles = await getPosterService('posterAttributes').importExternalFiles(
        attribute,
        tempFiles,
        onFileUploadedCb,
        provideAbortCb
      );

      this.importExternalFileCount--;
      return importedFiles;
    },
    /**
     * @param {object} attribute - the attribute whose options to retrieve
     * @param {object} parameters - search parameters
     * @param {object} parameters.context - context to send with the search
     * @returns {Promise<object[]>} options for the attribute
     */
    getPossibleValues(attribute, { context }) {
      return getPosterService('values').getPossibleValues(
        attribute,
        context,
        null,
        bodyParameters =>
          getPosterService('values').addAttributeValuesBodyParameters(
            bodyParameters,
            this.values,
            this.form.attributeGroups || []
          )
      );
    },
    // IMPORT
    /**
     * Triggers an import
     */
    onImport() {
      const fileBlobs = this.files.map(file => file.blob);

      services.getService('uploadManager').uploadFiles(
        fileBlobs,
        {
          status: Statutes.TO_BE_PRINTED,
          typeId: this.type.itemId,
          formatId: this.format.itemId,
          copies: this.copies,
          comment: this.comment,
          deadlineDate: Vue.filter('isoDateFull')(new Date()),
          source: DEFAULT_SIGNAGE_SOURCE,
          values: JSON.stringify(this.valuesToArray()),
        },
        POSTER_FILE_UPLOAD_DEFAULT_TASK,
        {
          notificationComponent: posterFileUploadNotification,
        }
      );

      this.$refs.dialog.close();
    },
  },
  /**
   * Component mounted hook
   */
  mounted() {
    this.init();
  },
};
</script>
